import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './DropdownManager.css';

const CATEGORY_LABELS = {
  LENDER: 'Lenders',
  FI_PROVIDER: 'F&I Providers',
  MOTORCYCLES: 'Motorcycles',
  SOURCE: 'Sources',
  DEAL_CLASS: 'Deal Classes',
  STATUS: 'Statuses',
  FIT_SPECIALIST: 'Fit Specialists',
  FI_MANAGER: 'F&I Managers',
  SALES_MANAGER: 'Sales Managers',
};

// These categories show archived options, reactivate, and upgrade-to-user
const EMPLOYEE_CATEGORIES = new Set(['FIT_SPECIALIST', 'FI_MANAGER', 'SALES_MANAGER']);

function SortableMakeItem({ make, children, onDeleteClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: make.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="make-group">
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

function SortableFamilyItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="family-group">
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

function SortableModelItem({ model, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: model.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

function SortableItem({ option, currentCategory, onSetDefault, onDeleteClick, onReactivateClick, onUpgradeClick }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: option.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`dropdown-option-item ${!option.isActive ? 'archived' : ''}`}
    >
      <div {...attributes} {...listeners} className="drag-handle">
        ⋮⋮
      </div>
      <div className="option-content">
        <span className="option-label">{option.label}</span>
        {!option.isActive && <span className="badge archived-badge">Archived</span>}
        {option.groupBy && <span className="badge group-badge">Make: {option.groupBy}</span>}
      </div>
      <div className="option-actions">
        {option.isActive ? (
          <>
            <button
              type="button"
              onClick={() => onSetDefault(option.value)}
              className={`icon-btn ${currentCategory?.defaultValue === option.value ? 'active' : ''}`}
              title="Set as default"
            >
              {currentCategory?.defaultValue === option.value ? '★' : '☆'}
            </button>
            {onUpgradeClick && (
              <button
                type="button"
                onClick={() => onUpgradeClick(option)}
                className="icon-btn upgrade-btn"
                title="Upgrade to user account"
              >
                ↑
              </button>
            )}
            <button
              type="button"
              onClick={() => onDeleteClick(option)}
              className="icon-btn delete-btn"
              title="Deactivate"
            >
              ✕
            </button>
          </>
        ) : (
          onReactivateClick && (
            <button
              type="button"
              onClick={() => onReactivateClick(option)}
              className="icon-btn reactivate-btn"
              title="Reactivate"
            >
              ↺
            </button>
          )
        )}
      </div>
    </div>
  );
}

const DropdownManager = ({ dealershipId, talonEnabled = false }) => {
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('LENDER');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [newOptionGroupBy, setNewOptionGroupBy] = useState('');
  const [newModelValue, setNewModelValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);
  const [expandedMakes, setExpandedMakes] = useState({});
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [editingOption, setEditingOption] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [makeOrder, setMakeOrder] = useState([]);
  const [familyOrders, setFamilyOrders] = useState({});
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [optionToUpgrade, setOptionToUpgrade] = useState(null);
  const [upgradeForm, setUpgradeForm] = useState({ name: '', email: '', username: '' });
  const [upgradeResult, setUpgradeResult] = useState(null);
  const [addingCategoryMake, setAddingCategoryMake] = useState(null);
  const [newCategoryValue, setNewCategoryValue] = useState('');
  const [editingFamily, setEditingFamily] = useState(null);
  const [editFamilyValue, setEditFamilyValue] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  // The item currently being dragged, so a DragOverlay can keep it visible
  // (and unclipped by category containers) while dragging between categories.
  const [activeDrag, setActiveDrag] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (dealershipId) {
      fetchCategories();
    }
  }, [dealershipId]);

  // Initialize make and family ordering state from fetched data
  useEffect(() => {
    if (activeTab !== 'MOTORCYCLES') return;
    const makesCategory = categories.find(c => c.category === 'MOTORCYCLE_MAKE');
    const modelsCategory = categories.find(c => c.category === 'MOTORCYCLE_MODEL');
    // Models category may not exist yet (e.g. makes added before any model) — treat as empty
    if (!makesCategory) return;

    const sortedMakes = [...(makesCategory.options || [])].sort((a, b) => a.displayOrder - b.displayOrder);
    setMakeOrder(sortedMakes.map(m => m.id));

    const modelOptions = (modelsCategory && modelsCategory.options) || [];
    const savedFamilyOrder = (modelsCategory && modelsCategory.metadata && modelsCategory.metadata.familyOrder) || {};
    const newFamilyOrders = {};
    sortedMakes.forEach(make => {
      const modelsForMake = modelOptions.filter(m => m.groupBy === make.value);
      const derivedFamilies = [...new Set(modelsForMake.map(m => m.familyGroup || 'Uncategorized'))];
      const saved = savedFamilyOrder[make.value];
      if (saved && saved.length > 0) {
        // Merge: saved order first, then any new families not yet in saved list
        const merged = [...saved, ...derivedFamilies.filter(f => !saved.includes(f))];
        newFamilyOrders[make.value] = merged;
      } else {
        newFamilyOrders[make.value] = derivedFamilies;
      }
    });
    setFamilyOrders(newFamilyOrders);
  }, [categories, activeTab]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns?includeArchived=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to fetch dropdown categories (${response.status})`);
      }

      const data = await response.json();
      console.log('Fetched categories:', data);
      setCategories(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentCategory = () => {
    return categories.find((cat) => cat.category === activeTab);
  };

  // Get motorcycle makes and models grouped together
  const getMotorcycleData = () => {
    const makesCategory = categories.find((cat) => cat.category === 'MOTORCYCLE_MAKE');
    const modelsCategory = categories.find((cat) => cat.category === 'MOTORCYCLE_MODEL');

    // Only the makes category is required; models may not exist until one is added
    if (!makesCategory) return { makes: [], models: [] };

    return {
      makes: makesCategory.options || [],
      models: (modelsCategory && modelsCategory.options) || [],
      makesCategory,
      modelsCategory
    };
  };

  const toggleMake = (makeValue) => {
    setExpandedMakes(prev => ({
      ...prev,
      [makeValue]: !prev[makeValue]
    }));
  };

  const toggleFamily = (makeValue, familyGroup) => {
    const key = `${makeValue}-${familyGroup}`;
    setExpandedFamilies(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleAddOption = async (categoryOverride = null, makeForModel = null, familyGroup = null) => {
    // Use newModelValue for model adds, newOptionValue for make adds
    const inputValue = makeForModel ? newModelValue : newOptionValue;

    if (!inputValue.trim()) {
      setError('Option value cannot be empty');
      return;
    }

    try {
      const payload = {
        value: inputValue.trim(),
        label: inputValue.trim(),
      };

      // For motorcycles tab, determine if we're adding a make or model
      let targetCategory = categoryOverride || activeTab;

      if (activeTab === 'MOTORCYCLES') {
        if (makeForModel) {
          targetCategory = 'MOTORCYCLE_MODEL';
          payload.groupBy = makeForModel;
          if (familyGroup) {
            payload.familyGroup = familyGroup;
          }
        } else {
          targetCategory = 'MOTORCYCLE_MAKE';
        }
      }

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/dealerships/${dealershipId}/dropdowns/${targetCategory}/options`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Failed to add option');
      }

      if (makeForModel) {
        setNewModelValue('');
      } else {
        setNewOptionValue('');
      }
      setNewOptionGroupBy('');
      setSuccess('Option added successfully');
      await fetchCategories();
    } catch (err) {
      console.error('Error adding option:', err);
      setError(err.message);
    }
  };

  const handleDeleteClick = (option) => {
    // Store both the option and which category it belongs to
    const categoryType = activeTab === 'MOTORCYCLES'
      ? (option.groupBy ? 'MOTORCYCLE_MODEL' : 'MOTORCYCLE_MAKE')
      : activeTab;

    setOptionToDelete({ ...option, _categoryType: categoryType });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!optionToDelete) return;

    try {
      const categoryForDelete = optionToDelete._categoryType || activeTab;
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/dealerships/${dealershipId}/dropdowns/${categoryForDelete}/options/${optionToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete option');
      }

      const result = await response.json();

      if (result.softDeleted) {
        setSuccess(
          `Option archived. Used in ${result.usageCount} sales records.`
        );
      } else {
        setSuccess('Option deleted successfully');
      }

      setDeleteDialogOpen(false);
      setOptionToDelete(null);
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting option:', err);
      setError(err.message);
    }
  };

  const handleReactivate = async (option) => {
    const categoryType = activeTab === 'MOTORCYCLES'
      ? (option.groupBy ? 'MOTORCYCLE_MODEL' : 'MOTORCYCLE_MAKE')
      : activeTab;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/dealerships/${dealershipId}/dropdowns/${categoryType}/options/${option.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ isActive: true }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reactivate option');
      }

      setSuccess('Option reactivated successfully');
      await fetchCategories();
    } catch (err) {
      console.error('Error reactivating option:', err);
      setError(err.message);
    }
  };

  const handleUpgradeClick = (option) => {
    setOptionToUpgrade(option);
    setUpgradeForm({ name: option.label, email: '', username: '' });
    setUpgradeResult(null);
    setUpgradeModalOpen(true);
  };

  const handleUpgradeSubmit = async () => {
    if (!optionToUpgrade) return;
    const { name, email, username } = upgradeForm;
    if (!name || !email || !username) {
      setError('Name, email, and username are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/dealerships/${dealershipId}/dropdowns/${activeTab}/options/${optionToUpgrade.id}/upgrade-to-user`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ name, email, username }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upgrade to user');
      }

      const data = await response.json();
      setUpgradeResult(data);
      await fetchCategories();
    } catch (err) {
      console.error('Error upgrading to user:', err);
      setError(err.message);
    }
  };

  const handleEditClick = (option) => {
    setEditingOption(option);
    setEditValue(option.label);
  };

  const handleEditSave = async () => {
    if (!editingOption || !editValue.trim()) {
      setError('Value cannot be empty');
      return;
    }

    try {
      const categoryType = activeTab === 'MOTORCYCLES'
        ? (editingOption.groupBy ? 'MOTORCYCLE_MODEL' : 'MOTORCYCLE_MAKE')
        : activeTab;

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/dealerships/${dealershipId}/dropdowns/${categoryType}/options/${editingOption.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            label: editValue.trim(),
            value: editValue.trim()
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update option');
      }

      setSuccess('Option updated successfully');
      setEditingOption(null);
      setEditValue('');
      await fetchCategories();
    } catch (err) {
      console.error('Error updating option:', err);
      setError(err.message);
    }
  };

  const handleEditCancel = () => {
    setEditingOption(null);
    setEditValue('');
  };

  const handleSetDefault = async (value, categoryOverride = null) => {
    // Determine the actual category - for motorcycles, it's always MOTORCYCLE_MODEL
    const targetCategory = categoryOverride || (activeTab === 'MOTORCYCLES' ? 'MOTORCYCLE_MODEL' : activeTab);
    const categoryData = categories.find(cat => cat.category === targetCategory);
    const newDefault = categoryData?.defaultValue === value ? null : value;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/dealerships/${dealershipId}/dropdowns/${targetCategory}/default`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ defaultValue: newDefault }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to set default value');
      }

      setSuccess(newDefault ? 'Default value set' : 'Default value cleared');
      await fetchCategories();
    } catch (err) {
      console.error('Error setting default:', err);
      setError(err.message);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const currentCategory = getCurrentCategory();
    if (!currentCategory) return;

    const oldIndex = currentCategory.options.findIndex((item) => item.id === active.id);
    const newIndex = currentCategory.options.findIndex((item) => item.id === over.id);

    const newItems = arrayMove(currentCategory.options, oldIndex, newIndex);

    setCategories((prev) =>
      prev.map((cat) =>
        cat.category === activeTab
          ? { ...cat, options: newItems }
          : cat
      )
    );

    try {
      const optionIds = newItems.map((item) => item.id);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/dealerships/${dealershipId}/dropdowns/${activeTab}/reorder`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ optionIds }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reorder options');
      }

      setSuccess('Options reordered');
    } catch (err) {
      console.error('Error reordering:', err);
      setError(err.message);
      await fetchCategories();
    }
  };

  // Helper: rebuild global model order with one family's models reordered
  const buildGlobalModelOrder = (allModels, makeValue, familyName, reorderedFamilyModels) => {
    const familyIds = new Set(reorderedFamilyModels.map(m => m.id));
    const sorted = [...allModels].sort((a, b) => a.displayOrder - b.displayOrder);
    const insertAt = sorted.findIndex(m => familyIds.has(m.id));
    const withoutFamily = sorted.filter(m => !familyIds.has(m.id));
    const result = [...withoutFamily];
    result.splice(insertAt === -1 ? result.length : insertAt, 0, ...reorderedFamilyModels);
    return result;
  };

  const handleMakeDragEnd = async (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIdx = makeOrder.indexOf(active.id);
    const newIdx = makeOrder.indexOf(over.id);
    const newOrder = arrayMove(makeOrder, oldIdx, newIdx);
    setMakeOrder(newOrder);

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns/MOTORCYCLE_MAKE/reorder`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ optionIds: newOrder }),
      });
      setSuccess('Makes reordered');
    } catch (err) {
      setError(err.message);
      await fetchCategories();
    }
  };

  const handleFamilyDragEnd = async (makeValue, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const prefix = `family-${makeValue}-`;
    const activeFamily = active.id.slice(prefix.length);
    const overFamily = over.id.slice(prefix.length);
    const currentFamilies = familyOrders[makeValue] || [];
    const newFamilies = arrayMove(currentFamilies, currentFamilies.indexOf(activeFamily), currentFamilies.indexOf(overFamily));
    setFamilyOrders(prev => ({ ...prev, [makeValue]: newFamilies }));

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns/MOTORCYCLE_MODEL/reorder-families`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ makeName: makeValue, familyOrder: newFamilies }),
      });
      setSuccess('Families reordered');
    } catch (err) {
      setError(err.message);
      await fetchCategories();
    }
  };

  const handleModelDragEnd = async (makeValue, familyName, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const modelsCategory = categories.find(c => c.category === 'MOTORCYCLE_MODEL');
    if (!modelsCategory) return;
    const allModels = modelsCategory.options || [];

    const familyModels = allModels
      .filter(m => m.groupBy === makeValue && (m.familyGroup || 'Uncategorized') === familyName)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    const oldIdx = familyModels.findIndex(m => m.id === active.id);
    const newIdx = familyModels.findIndex(m => m.id === over.id);
    const reorderedFamily = arrayMove(familyModels, oldIdx, newIdx);

    const newAllModels = buildGlobalModelOrder(allModels, makeValue, familyName, reorderedFamily);

    // Optimistic update
    setCategories(prev => prev.map(cat =>
      cat.category === 'MOTORCYCLE_MODEL' ? { ...cat, options: newAllModels } : cat
    ));

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns/MOTORCYCLE_MODEL/reorder`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ optionIds: newAllModels.map(m => m.id) }),
      });
      setSuccess('Models reordered');
    } catch (err) {
      setError(err.message);
      await fetchCategories();
    }
  };

  // Persist a make's category (family) order to the backend (metadata.familyOrder)
  const persistFamilyOrder = async (makeValue, familyOrder) => {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_URL}/api/dealerships/${dealershipId}/dropdowns/MOTORCYCLE_MODEL/reorder-families`,
      {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ makeName: makeValue, familyOrder }),
      }
    );
    if (!response.ok) throw new Error('Failed to save categories');
  };

  // Add a new (empty) category under a make
  const handleAddCategory = async (makeValue) => {
    const name = newCategoryValue.trim();
    if (!name) {
      setError('Category name cannot be empty');
      return;
    }
    const current = familyOrders[makeValue] || [];
    if (current.some((f) => f.toLowerCase() === name.toLowerCase())) {
      setError('A category with that name already exists for this make');
      return;
    }
    const newFamilies = [...current, name];
    setFamilyOrders((prev) => ({ ...prev, [makeValue]: newFamilies }));
    setNewCategoryValue('');
    setAddingCategoryMake(null);
    try {
      await persistFamilyOrder(makeValue, newFamilies);
      setSuccess('Category added');
    } catch (err) {
      setError(err.message);
      await fetchCategories();
    }
  };

  const handleFamilyEditClick = (makeValue, familyName) => {
    setEditingFamily({ make: makeValue, name: familyName });
    setEditFamilyValue(familyName);
  };

  const handleFamilyEditCancel = () => {
    setEditingFamily(null);
    setEditFamilyValue('');
  };

  // Rename a category: move every model in it to the new name, then update saved order
  const handleFamilyEditSave = async (makeValue) => {
    if (!editingFamily) return;
    const oldName = editingFamily.name;
    const newName = editFamilyValue.trim();
    if (!newName) {
      setError('Category name cannot be empty');
      return;
    }
    if (newName === oldName) {
      handleFamilyEditCancel();
      return;
    }
    const current = familyOrders[makeValue] || [];
    if (current.some((f) => f.toLowerCase() === newName.toLowerCase())) {
      setError('A category with that name already exists for this make');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const modelsCategory = categories.find((c) => c.category === 'MOTORCYCLE_MODEL');
      const modelsToMove = (modelsCategory?.options || []).filter(
        (m) => m.groupBy === makeValue && (m.familyGroup || 'Uncategorized') === oldName
      );
      await Promise.all(
        modelsToMove.map((m) =>
          fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns/MOTORCYCLE_MODEL/options/${m.id}`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ familyGroup: newName }),
          })
        )
      );
      const newFamilies = current.map((f) => (f === oldName ? newName : f));
      await persistFamilyOrder(makeValue, newFamilies);
      handleFamilyEditCancel();
      setSuccess('Category renamed');
      await fetchCategories();
    } catch (err) {
      setError(err.message);
      await fetchCategories();
    }
  };

  // Delete a category. Empty → remove silently. Non-empty → confirm first.
  const handleFamilyDeleteClick = (makeValue, familyName, familyModels) => {
    if (familyModels.length === 0) {
      removeCategory(makeValue, familyName);
    } else {
      setCategoryToDelete({ make: makeValue, name: familyName, models: familyModels });
    }
  };

  const removeCategory = async (makeValue, familyName) => {
    const current = familyOrders[makeValue] || [];
    const newFamilies = current.filter((f) => f !== familyName);
    setFamilyOrders((prev) => ({ ...prev, [makeValue]: newFamilies }));
    try {
      await persistFamilyOrder(makeValue, newFamilies);
      setSuccess('Category deleted');
    } catch (err) {
      setError(err.message);
      await fetchCategories();
    }
  };

  const handleCategoryDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    const { make, name, models } = categoryToDelete;
    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        models.map((m) =>
          fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns/MOTORCYCLE_MODEL/options/${m.id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include',
          })
        )
      );
      const current = familyOrders[make] || [];
      await persistFamilyOrder(make, current.filter((f) => f !== name));
      setCategoryToDelete(null);
      setSuccess('Category deleted');
      await fetchCategories();
    } catch (err) {
      setError(err.message);
      setCategoryToDelete(null);
      await fetchCategories();
    }
  };

  // Unified drag handler for a make's tree: routes family reorder, in-category
  // model reorder, and cross-category model moves through one DndContext.
  // Capture what's being dragged inside a make's tree (a family header or a model)
  // so the DragOverlay can render a floating clone that stays visible across categories.
  const handleMakeTreeDragStart = (makeValue, event) => {
    const id = event.active.id;
    const familyPrefix = `family-${makeValue}-`;
    if (String(id).startsWith(familyPrefix)) {
      setActiveDrag({ type: 'family', label: String(id).slice(familyPrefix.length) });
      return;
    }
    const modelsCategory = categories.find((c) => c.category === 'MOTORCYCLE_MODEL');
    const model = (modelsCategory?.options || []).find((m) => m.id === id);
    if (model) setActiveDrag({ type: 'model', label: model.label });
  };

  const handleMakeDragStart = (event) => {
    const make = (motorcycleData?.makes || []).find((m) => m.id === event.active.id);
    if (make) setActiveDrag({ type: 'make', label: make.label });
  };

  const handleMakeTreeDragEnd = async (makeValue, event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const familyPrefix = `family-${makeValue}-`;
    const activeIsFamily = String(active.id).startsWith(familyPrefix);

    if (activeIsFamily) {
      // Only reorder when dropped onto another category header
      if (String(over.id).startsWith(familyPrefix)) {
        return handleFamilyDragEnd(makeValue, event);
      }
      return;
    }

    // Active item is a model
    const modelsCategory = categories.find((c) => c.category === 'MOTORCYCLE_MODEL');
    if (!modelsCategory) return;
    const allModels = modelsCategory.options || [];
    const activeModel = allModels.find((m) => m.id === active.id);
    if (!activeModel) return;
    const sourceFamily = activeModel.familyGroup || 'Uncategorized';

    let targetFamily;
    let overModelId = null;
    if (String(over.id).startsWith(familyPrefix)) {
      targetFamily = String(over.id).slice(familyPrefix.length);
    } else {
      const overModel = allModels.find((m) => m.id === over.id);
      if (!overModel) return;
      targetFamily = overModel.familyGroup || 'Uncategorized';
      overModelId = overModel.id;
    }

    if (sourceFamily === targetFamily) {
      return handleModelDragEnd(makeValue, sourceFamily, event);
    }

    // Cross-category move: reassign familyGroup and reposition
    const newFamilyGroup = targetFamily === 'Uncategorized' ? null : targetFamily;
    const sorted = [...allModels].sort((a, b) => a.displayOrder - b.displayOrder);
    const without = sorted.filter((m) => m.id !== active.id);
    const movedModel = { ...activeModel, familyGroup: newFamilyGroup };

    let insertAt;
    if (overModelId) {
      insertAt = without.findIndex((m) => m.id === overModelId);
    } else {
      // Dropped on the category header: append after the last model already in it
      let lastIdx = -1;
      without.forEach((m, i) => {
        if (m.groupBy === makeValue && (m.familyGroup || 'Uncategorized') === targetFamily) {
          lastIdx = i;
        }
      });
      insertAt = lastIdx === -1 ? -1 : lastIdx + 1;
    }
    const result = [...without];
    result.splice(insertAt === -1 ? result.length : insertAt, 0, movedModel);

    // Optimistic update
    setCategories((prev) =>
      prev.map((cat) => (cat.category === 'MOTORCYCLE_MODEL' ? { ...cat, options: result } : cat))
    );

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns/MOTORCYCLE_MODEL/options/${active.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ familyGroup: newFamilyGroup }),
      });
      await fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns/MOTORCYCLE_MODEL/reorder`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ optionIds: result.map((m) => m.id) }),
      });
      setSuccess(`Moved to ${targetFamily}`);
      await fetchCategories();
    } catch (err) {
      setError(err.message);
      await fetchCategories();
    }
  };

  if (loading) {
    return <div className="dropdown-manager-loading">Loading dropdown options...</div>;
  }

  const currentCategory = getCurrentCategory();
  const motorcycleData = activeTab === 'MOTORCYCLES' ? getMotorcycleData() : null;

  // Floating clone rendered by DragOverlay so the dragged item stays visible
  // (and unclipped by its category container) while moving between categories.
  const renderDragOverlay = () => (
    <DragOverlay>
      {activeDrag ? (
        <div className={`drag-overlay-item drag-overlay-${activeDrag.type}`}>
          <span className="drag-handle">⋮⋮</span>
          <span className="drag-overlay-label">{activeDrag.label}</span>
        </div>
      ) : null}
    </DragOverlay>
  );

  return (
    <div className="dropdown-manager">
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      <div className="dropdown-tabs">
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`tab ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="dropdown-content">
        {activeTab === 'MOTORCYCLES' ? (
          // Motorcycle Tree View
          <>
            <div className="add-option-form">
              <input
                type="text"
                placeholder="New make (e.g., Harley-Davidson, Honda)"
                value={newOptionValue}
                onChange={(e) => setNewOptionValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
              />
              <button type="button" onClick={() => handleAddOption()} className="btn-primary">
                + Add Make
              </button>
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleMakeDragStart} onDragEnd={handleMakeDragEnd} onDragCancel={() => setActiveDrag(null)}>
              <SortableContext items={makeOrder} strategy={verticalListSortingStrategy}>
                <div className="motorcycle-tree">
                  {makeOrder.map(makeId => {
                    const make = motorcycleData.makes.find(m => m.id === makeId);
                    if (!make) return null;
                    const models = motorcycleData.models.filter(m => m.groupBy === make.value);
                    const isExpanded = expandedMakes[make.value];
                    const families = familyOrders[make.value] || [];

                    return (
                      <SortableMakeItem key={make.id} make={make} onDeleteClick={handleDeleteClick}>
                        {({ dragHandleProps }) => (
                          <>
                            <div className={`make-item ${!make.isActive ? 'archived' : ''}`}>
                              <div className="make-content">
                                <div {...dragHandleProps} className="drag-handle">⋮⋮</div>
                                <button
                                  type="button"
                                  className="expand-btn"
                                  onClick={() => toggleMake(make.value)}
                                >
                                  {isExpanded ? '▼' : '▶'}
                                </button>
                                <span className="make-label">{make.label}</span>
                                {!make.isActive && <span className="badge archived-badge">Archived</span>}
                                <span className="model-count">({models.length} models)</span>
                              </div>
                              <div className="make-actions">
                                {make.isActive ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteClick(make)}
                                    className="icon-btn delete-btn"
                                    title="Delete make"
                                  >
                                    ✕
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleReactivate(make)}
                                    className="icon-btn reactivate-btn"
                                    title="Reactivate make"
                                  >
                                    ↺
                                  </button>
                                )}
                              </div>
                            </div>

                            {isExpanded && (
                              <DndContext sensors={sensors} collisionDetection={closestCorners}
                                onDragStart={(e) => handleMakeTreeDragStart(make.value, e)}
                                onDragEnd={(e) => handleMakeTreeDragEnd(make.value, e)}
                                onDragCancel={() => setActiveDrag(null)}>
                                <SortableContext
                                  items={families.map(f => `family-${make.value}-${f}`)}
                                  strategy={verticalListSortingStrategy}
                                >
                                  <div className="families-list">
                                    {families.map(familyName => {
                                      const familyModels = models
                                        .filter(m => (m.familyGroup || 'Uncategorized') === familyName)
                                        .sort((a, b) => a.displayOrder - b.displayOrder);
                                      const familyKey = `${make.value}-${familyName}`;
                                      const isFamilyExpanded = expandedFamilies[familyKey];

                                      return (
                                        <SortableFamilyItem key={familyName} id={`family-${make.value}-${familyName}`}>
                                          {({ dragHandleProps: familyDragHandleProps }) => (
                                            <>
                                              <div className="family-item">
                                                {editingFamily && editingFamily.make === make.value && editingFamily.name === familyName ? (
                                                  <>
                                                    <div className="family-content">
                                                      <div {...familyDragHandleProps} className="drag-handle">⋮⋮</div>
                                                      <input
                                                        type="text"
                                                        value={editFamilyValue}
                                                        onChange={(e) => setEditFamilyValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                          if (e.key === 'Enter') handleFamilyEditSave(make.value);
                                                          if (e.key === 'Escape') handleFamilyEditCancel();
                                                        }}
                                                        className="edit-input"
                                                        autoFocus
                                                      />
                                                    </div>
                                                    <div className="family-actions">
                                                      <button type="button" onClick={() => handleFamilyEditSave(make.value)} className="icon-btn save-btn" title="Save">✓</button>
                                                      <button type="button" onClick={handleFamilyEditCancel} className="icon-btn cancel-btn" title="Cancel">✕</button>
                                                    </div>
                                                  </>
                                                ) : (
                                                  <>
                                                    <div className="family-content">
                                                      <div {...familyDragHandleProps} className="drag-handle">⋮⋮</div>
                                                      <button
                                                        type="button"
                                                        className="expand-btn"
                                                        onClick={() => toggleFamily(make.value, familyName)}
                                                      >
                                                        {isFamilyExpanded ? '▼' : '▶'}
                                                      </button>
                                                      <span className="family-label">{familyName}</span>
                                                      <span className="model-count">({familyModels.length} models)</span>
                                                    </div>
                                                    <div className="family-actions">
                                                      <button
                                                        type="button"
                                                        onClick={() => {
                                                          setNewOptionValue('');
                                                          setNewOptionGroupBy(`${make.value}|${familyName}`);
                                                          setExpandedFamilies(prev => ({
                                                            ...prev,
                                                            [`${make.value}-${familyName}`]: true
                                                          }));
                                                        }}
                                                        className="icon-btn add-model-btn"
                                                        title={`Add model to ${familyName}`}
                                                      >
                                                        +
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() => handleFamilyEditClick(make.value, familyName)}
                                                        className="icon-btn edit-btn"
                                                        title="Rename category"
                                                      >
                                                        ✎
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() => handleFamilyDeleteClick(make.value, familyName, familyModels)}
                                                        className="icon-btn delete-btn"
                                                        title="Delete category"
                                                      >
                                                        ✕
                                                      </button>
                                                    </div>
                                                  </>
                                                )}
                                              </div>

                                              {isFamilyExpanded && (
                                                <SortableContext
                                                  items={familyModels.map(m => m.id)}
                                                  strategy={verticalListSortingStrategy}
                                                >
                                                    <div className="models-list">
                                                      {newOptionGroupBy === `${make.value}|${familyName}` && (
                                                        <div className="add-model-inline">
                                                          <input
                                                            type="text"
                                                            placeholder={`New ${familyName} model`}
                                                            value={newModelValue}
                                                            onChange={(e) => setNewModelValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                              if (e.key === 'Enter') {
                                                                handleAddOption('MOTORCYCLE_MODEL', make.value, familyName);
                                                              }
                                                            }}
                                                            autoFocus
                                                          />
                                                          <button
                                                            type="button"
                                                            onClick={() => handleAddOption('MOTORCYCLE_MODEL', make.value, familyName)}
                                                            className="btn-primary btn-sm"
                                                          >
                                                            Add
                                                          </button>
                                                          <button
                                                            type="button"
                                                            onClick={() => {
                                                              setNewModelValue('');
                                                              setNewOptionGroupBy('');
                                                            }}
                                                            className="btn-cancel btn-sm"
                                                          >
                                                            Cancel
                                                          </button>
                                                        </div>
                                                      )}

                                                      {familyModels.map((model) => (
                                                        <SortableModelItem key={model.id} model={model}>
                                                          {({ dragHandleProps: modelDragHandleProps }) => (
                                                            <div className={`model-item ${!model.isActive ? 'archived' : ''}`}>
                                                              {editingOption?.id === model.id ? (
                                                                <>
                                                                  <input
                                                                    type="text"
                                                                    value={editValue}
                                                                    onChange={(e) => setEditValue(e.target.value)}
                                                                    onKeyDown={(e) => e.key === 'Enter' && handleEditSave()}
                                                                    className="edit-input"
                                                                    autoFocus
                                                                  />
                                                                  <div className="model-actions">
                                                                    <button type="button" onClick={handleEditSave} className="icon-btn save-btn" title="Save">✓</button>
                                                                    <button type="button" onClick={handleEditCancel} className="icon-btn cancel-btn" title="Cancel">✕</button>
                                                                  </div>
                                                                </>
                                                              ) : (
                                                                <>
                                                                  <div {...modelDragHandleProps} className="drag-handle">⋮⋮</div>
                                                                  <span className="model-label">{model.label}</span>
                                                                  {!model.isActive && <span className="badge archived-badge">Archived</span>}
                                                                  <div className="model-actions">
                                                                    {model.isActive ? (
                                                                      <>
                                                                        <button
                                                                          type="button"
                                                                          onClick={() => handleSetDefault(model.value)}
                                                                          className={`icon-btn ${motorcycleData.modelsCategory?.defaultValue === model.value ? 'active' : ''}`}
                                                                          title="Set as default"
                                                                        >
                                                                          {motorcycleData.modelsCategory?.defaultValue === model.value ? '★' : '☆'}
                                                                        </button>
                                                                        <button type="button" onClick={() => handleEditClick(model)} className="icon-btn edit-btn" title="Edit model">✎</button>
                                                                        <button type="button" onClick={() => handleDeleteClick(model)} className="icon-btn delete-btn" title="Delete model">✕</button>
                                                                      </>
                                                                    ) : (
                                                                      <button type="button" onClick={() => handleReactivate(model)} className="icon-btn reactivate-btn" title="Reactivate model">↺</button>
                                                                    )}
                                                                  </div>
                                                                </>
                                                              )}
                                                            </div>
                                                          )}
                                                        </SortableModelItem>
                                                      ))}
                                                    </div>
                                                  </SortableContext>
                                              )}
                                            </>
                                          )}
                                        </SortableFamilyItem>
                                      );
                                    })}

                                    {addingCategoryMake === make.value ? (
                                      <div className="add-category-inline">
                                        <input
                                          type="text"
                                          placeholder="New category (e.g., Sportster, Cruiser)"
                                          value={newCategoryValue}
                                          onChange={(e) => setNewCategoryValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddCategory(make.value);
                                            if (e.key === 'Escape') { setAddingCategoryMake(null); setNewCategoryValue(''); }
                                          }}
                                          autoFocus
                                        />
                                        <button
                                          type="button"
                                          onClick={() => handleAddCategory(make.value)}
                                          className="btn-primary btn-sm"
                                        >
                                          Add
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => { setAddingCategoryMake(null); setNewCategoryValue(''); }}
                                          className="btn-cancel btn-sm"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="add-category-btn"
                                        onClick={() => {
                                          setAddingCategoryMake(make.value);
                                          setNewCategoryValue('');
                                        }}
                                      >
                                        + Add category
                                      </button>
                                    )}

                                    {families.length === 0 && addingCategoryMake !== make.value && (
                                      <div className="empty-models">No categories yet</div>
                                    )}
                                  </div>
                                </SortableContext>
                                {createPortal(renderDragOverlay(), document.body)}
                              </DndContext>
                            )}
                          </>
                        )}
                      </SortableMakeItem>
                    );
                  })}

                  {motorcycleData.makes.length === 0 && (
                    <div className="empty-state">
                      No makes yet. Add one above to get started.
                    </div>
                  )}
                </div>
              </SortableContext>
              {createPortal(renderDragOverlay(), document.body)}
            </DndContext>
          </>
        ) : (
          // Normal List View
          <>
            <div className="add-option-form">
              <input
                type="text"
                placeholder="New option value"
                value={newOptionValue}
                onChange={(e) => setNewOptionValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
              />
              <button type="button" onClick={() => handleAddOption()} className="btn-primary">
                + Add
              </button>
            </div>

            {(() => {
              const isEmployeeCategory = EMPLOYEE_CATEGORIES.has(activeTab);
              const displayOptions = isEmployeeCategory
                ? (currentCategory?.options || [])
                : (currentCategory?.options || []).filter(o => o.isActive);
              return (
                <>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={displayOptions.map(o => o.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="options-list">
                        {displayOptions.map((option) => (
                          <SortableItem
                            key={option.id}
                            option={option}
                            currentCategory={currentCategory}
                            onSetDefault={handleSetDefault}
                            onDeleteClick={handleDeleteClick}
                            onReactivateClick={isEmployeeCategory ? handleReactivate : undefined}
                            onUpgradeClick={isEmployeeCategory && talonEnabled ? handleUpgradeClick : undefined}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>

                  {displayOptions.length === 0 && (
                    <div className="empty-state">
                      No options yet. Add one above to get started.
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}
      </div>

      {deleteDialogOpen && createPortal(
        <div className="dm-dialog-overlay" onClick={() => setDeleteDialogOpen(false)}>
          <div className="dm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{EMPLOYEE_CATEGORIES.has(activeTab) ? 'Deactivate Employee' : 'Delete Option'}</h3>
            <p>Are you sure you want to {EMPLOYEE_CATEGORIES.has(activeTab) ? 'deactivate' : 'delete'} "{optionToDelete?.label}"?</p>
            <p className="dm-dialog-hint">
              {EMPLOYEE_CATEGORIES.has(activeTab)
                ? 'This employee will no longer appear in dropdown menus but will remain in existing records.'
                : 'If this option is in use, it will be archived instead of deleted.'}
            </p>
            <div className="dm-dialog-actions">
              <button onClick={() => setDeleteDialogOpen(false)}>Cancel</button>
              <button onClick={handleDeleteConfirm} className="btn-danger">
                {EMPLOYEE_CATEGORIES.has(activeTab) ? 'Deactivate' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {upgradeModalOpen && createPortal(
        <div className="dm-dialog-overlay" onClick={() => { setUpgradeModalOpen(false); setUpgradeResult(null); }}>
          <div className="dm-dialog dm-dialog-upgrade" onClick={(e) => e.stopPropagation()}>
            {upgradeResult ? (
              <>
                <h3>User Created</h3>
                <p>"{optionToUpgrade?.label}" has been upgraded to a user account and {upgradeResult.activitiesMigrated} record(s) were migrated.</p>
                <p className="dm-dialog-hint upgrade-temp-password">
                  Temporary password: <strong>{upgradeResult.tempPassword}</strong><br />
                  Share this with the employee so they can log in and change it.
                </p>
                <div className="dm-dialog-actions">
                  <button onClick={() => { setUpgradeModalOpen(false); setUpgradeResult(null); }}>Close</button>
                </div>
              </>
            ) : (
              <>
                <h3>Upgrade to User Account</h3>
                <p>Create a login account for "{optionToUpgrade?.label}". Existing sales records will be migrated to the new user.</p>
                <div className="upgrade-form">
                  <label>Name</label>
                  <input
                    type="text"
                    value={upgradeForm.name}
                    onChange={(e) => setUpgradeForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Full name"
                  />
                  <label>Email</label>
                  <input
                    type="email"
                    value={upgradeForm.email}
                    onChange={(e) => setUpgradeForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="Email address"
                  />
                  <label>Username</label>
                  <input
                    type="text"
                    value={upgradeForm.username}
                    onChange={(e) => setUpgradeForm(f => ({ ...f, username: e.target.value }))}
                    placeholder="Username"
                  />
                </div>
                <div className="dm-dialog-actions">
                  <button onClick={() => { setUpgradeModalOpen(false); setUpgradeResult(null); }}>Cancel</button>
                  <button onClick={handleUpgradeSubmit} className="btn-primary">Create User</button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {categoryToDelete && createPortal(
        <div className="dm-dialog-overlay" onClick={() => setCategoryToDelete(null)}>
          <div className="dm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Category</h3>
            <p>
              Delete the category "{categoryToDelete.name}" and all {categoryToDelete.models.length}{' '}
              model{categoryToDelete.models.length === 1 ? '' : 's'} inside it?
            </p>
            <p className="dm-dialog-hint">
              Any models that are used in existing sales records will be archived instead of deleted.
            </p>
            <div className="dm-dialog-actions">
              <button onClick={() => setCategoryToDelete(null)}>Cancel</button>
              <button onClick={handleCategoryDeleteConfirm} className="btn-danger">
                Delete Category
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DropdownManager;
